import { PenaltyDescriptionId } from '@/warranty/domain/entities/penalty-description-id';
import type { PenaltyOverviewChartData } from '@/warranty/domain/entities/warranty-penalty-chart-data';

import {
  type BatteryLevel,
  DIM_BY_LEVEL,
} from '../../constants/battery-levels';

export interface PenaltyChartRow {
  [key: string]: string | number | undefined | null;
  'platform_core_warranty_penalty_unit_alerts.description_id': string;
  'platform_core_warranty_penalty_unit_alerts.penalty_historical_agg': number;
  'platform_core_warranty_penalty_unit_alerts.penalty_future_agg': number;
  'platform_core_warranty_penalty_unit_alerts.last_detection_future':
    | string
    | null;
}

/**
 * Aggregate Looker rows into the chart data structure.
 */
export function transformPenaltyChartRows(
  rows: PenaltyChartRow[],
  level: BatteryLevel | 'site',
): PenaltyOverviewChartData[] {
  const map = new Map<string, PenaltyOverviewChartData>();

  rows.forEach((row) => {
    const idField =
      level === 'site'
        ? DIM_BY_LEVEL.site
        : DIM_BY_LEVEL[level as BatteryLevel];
    const fullFrontendId = row[idField] as string | undefined;

    if (!fullFrontendId) return; // skip incomplete rows

    if (!map.has(fullFrontendId)) {
      map.set(fullFrontendId, {
        id: fullFrontendId,
        groupFullId: '', // TODO: populate once available in Looker
        topFullId: '',
        unitFullId: undefined,
        fullFrontendId,
        penalties: Object.values(PenaltyDescriptionId).map((info) => ({
          type: info,
          yearToDate: 0,
          futurePrediction: 0,
          windowEndDate: undefined,
        })),
      });
    }

    const penaltyHistoricalValue = Number(
      row[
        'platform_core_warranty_penalty_unit_alerts.penalty_historical_agg'
      ] ?? 0,
    );
    const penaltyFutureValue = Number(
      row['platform_core_warranty_penalty_unit_alerts.penalty_future_agg'] ?? 0,
    );

    // Parse window end date from last_detection_future
    const lastDetectionFutureValue =
      row['platform_core_warranty_penalty_unit_alerts.last_detection_future'];
    const windowEndDate = lastDetectionFutureValue
      ? new Date(lastDetectionFutureValue)
      : undefined;

    const entry = map.get(fullFrontendId)!;
    const penalty = entry.penalties.find(
      (p) =>
        p.type ===
        (row[
          'platform_core_warranty_penalty_unit_alerts.description_id'
        ] as PenaltyDescriptionId),
    );

    if (penalty) {
      penalty.yearToDate = Math.max(penalty.yearToDate, penaltyHistoricalValue);
      penalty.futurePrediction = Math.max(
        penalty.futurePrediction,
        penaltyFutureValue,
      );
      // Use the latest window end date if multiple exist
      if (
        windowEndDate &&
        (!penalty.windowEndDate || windowEndDate > penalty.windowEndDate)
      ) {
        penalty.windowEndDate = windowEndDate;
      }
    }
  });

  return Array.from(map.values());
}
